import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export function Hero({ image, eyebrow, title, subtitle, height = "70vh", align = "bottom", cta }) {
  const justify = align === "top" ? "justify-start pt-28 md:pt-32" : "justify-end pb-16 md:pb-24";
  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ minHeight: height }}
      data-testid="page-hero"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="absolute inset-0 hero-scrim" />
      <div className="grain" />
      <div className={`relative nsa-container flex flex-col ${justify}`} style={{ minHeight: height }}>
        {eyebrow && (
          <motion.span
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="label-eyebrow text-white/95"
            style={{ textShadow: "0 2px 12px rgba(0,0,0,0.55)" }}
          >
            {eyebrow}
          </motion.span>
        )}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-display font-bold text-white uppercase tracking-tight text-5xl sm:text-6xl md:text-7xl leading-[0.95] mt-4 max-w-4xl"
          style={{ textShadow: "0 4px 24px rgba(0,0,0,0.5)" }}
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-white/95 mt-5 max-w-2xl text-lg md:text-xl font-body"
            style={{ textShadow: "0 2px 12px rgba(0,0,0,0.5)" }}
          >
            {subtitle}
          </motion.p>
        )}
        {cta && (
          <motion.a
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            href={cta.href}
            target={cta.external ? "_blank" : undefined}
            rel={cta.external ? "noopener noreferrer" : undefined}
            className="mt-7 inline-flex items-center gap-3 label-eyebrow px-6 py-4 self-start transition-colors"
            style={{
              background: cta.color || "#111827",
              color: "#ffffff",
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            }}
            data-testid="hero-cta-btn"
          >
            {cta.label} <ArrowRight size={16} />
          </motion.a>
        )}
      </div>
    </section>
  );
}

export function Section({ children, className = "", id, bleed = false, tone }) {
  if (bleed || tone) {
    // Full-bleed background; content stays in the container
    const bg =
      tone === "dark" ? "bg-gray-900 text-white"
      : tone === "gray" ? "bg-gray-50"
      : "";
    return (
      <section id={id} className={`w-full ${bg} ${className}`}>
        <div className="nsa-container py-20 md:py-28">{children}</div>
      </section>
    );
  }
  return (
    <section id={id} className={`nsa-container py-20 md:py-28 ${className}`}>
      {children}
    </section>
  );
}

export function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="max-w-3xl mb-12 md:mb-16">
      {eyebrow && <span className="label-eyebrow text-[color:var(--nsa-orange)]">{eyebrow}</span>}
      <h2 className="font-display uppercase tracking-tight font-bold text-4xl md:text-5xl mt-3">{title}</h2>
      {description && <p className="text-gray-600 mt-4 text-lg leading-relaxed">{description}</p>}
    </div>
  );
}

export function Bento({ items }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6">
      {items.map((it, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: i * 0.05 }}
          style={it.tone === "dark" ? { background: "#111827", color: "#fff", borderColor: "#111827" } : undefined}
          className={`card-flat p-6 md:p-8 ${it.span || "md:col-span-2"}`}
        >
          {it.icon && (
            <div className={`flex items-center justify-center h-12 w-12 mb-5 ${it.tone === "dark" ? "bg-[color:var(--nsa-orange)] text-white" : "bg-gray-100 text-[color:var(--nsa-orange)]"}`}>
              {it.icon}
            </div>
          )}
          {it.eyebrow && (
            <span className={`label-eyebrow ${it.tone === "dark" ? "text-[color:var(--nsa-orange)]" : "text-gray-500"}`}>
              {it.eyebrow}
            </span>
          )}
          <h3 className="font-display uppercase tracking-tight text-2xl md:text-3xl mt-2">{it.title}</h3>
          {it.body && <p className={`mt-3 leading-relaxed ${it.tone === "dark" ? "text-white/75" : "text-gray-600"}`}>{it.body}</p>}
          {it.image && (
            <div
              className="mt-6 aspect-[4/3] bg-cover bg-center"
              style={{ backgroundImage: `url(${it.image})` }}
            />
          )}
        </motion.div>
      ))}
    </div>
  );
}
